import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { Button, Label, Input, ErrorMessage, FlexContainer } from '../styles/GlobalStyles';
import { invitationService } from '../services/invitationService';
import { teamService } from '../services/teamService';

const FormContainer = styled.div`
  background: var(--card-background);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
`;

const FormTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: var(--spacing-md);
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-lg);
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  font-size: 0.875rem;
  color: #166534;
  
  &::before {
    content: '✓';
    margin-right: var(--spacing-xs);
    font-weight: bold;
  }
`;

const InfoMessage = styled.div`
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  font-size: 0.875rem;
  color: #0c4a6e;
  
  &::before {
    content: 'ℹ️';
    margin-right: var(--spacing-xs);
  }
`;

const CoachInfo = styled.div`
  background: var(--background-secondary);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
`;

const CoachLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xs);
`;

const CoachName = styled.div`
  font-size: 1rem;
  color: var(--text-primary);
  font-weight: 500;
`;

interface InviteCoachFormData {
  firstName: string;
  lastName: string;
  email: string;
}

interface InviteCoachFormProps {
  teamId: string;
  existingCollaborator?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  pendingInvitation?: {
    email: string;
    firstName: string;
    lastName: string;
    expiresAt: string;
  } | null;
  onInviteSent?: () => void;
  onCollaboratorRemoved?: () => void;
  onInvitationCancelled?: () => void;
  isMainCoach?: boolean;
}

export const InviteCoachForm: React.FC<InviteCoachFormProps> = ({
  teamId,
  existingCollaborator,
  pendingInvitation,
  onInviteSent,
  onCollaboratorRemoved,
  onInvitationCancelled,
  isMainCoach = true,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isRemoving, setIsRemoving] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteCoachFormData>();

  const onSubmit = async (data: InviteCoachFormData) => {
    setIsLoading(true);
    setServerError('');
    setSuccessMessage('');

    try {
      const response = await invitationService.createInvitation({
        teamId,
        invitedFirstName: data.firstName,
        invitedLastName: data.lastName,
        invitedEmail: data.email,
      });
      
      const expirationDate = new Date(response.expiresAt);
      
      setSuccessMessage(
        `Invitation sent to ${data.email}! The invitation will expire on ${expirationDate.toLocaleDateString()}.`
      );
      reset();
      
      if (onInviteSent) {
        onInviteSent();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to send invitation';
      const errorCode = error.response?.data?.error?.code;
      
      // Handle specific error codes
      if (errorCode === 'RATE_LIMIT_EXCEEDED' || errorMessage.includes('rate limit')) {
        setServerError('You have sent too many invitations recently. Please try again later.');
      } else if (errorCode === 'COLLABORATOR_LIMIT_REACHED' || errorMessage.includes('already has a collaborating coach')) {
        setServerError('This team already has a collaborating coach.');
      } else if (errorCode === 'INVITATION_ALREADY_EXISTS' || errorMessage.includes('pending invitation')) {
        setServerError('There is already a pending invitation for this team.');
      } else {
        setServerError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveCollaborator = async () => {
    if (!existingCollaborator || !isMainCoach) return;

    const confirmed = window.confirm(
      `Are you sure you want to remove ${existingCollaborator.firstName} ${existingCollaborator.lastName} as a collaborator? They will lose access to this team.`
    );

    if (!confirmed) return;

    setIsRemoving(true);
    setServerError('');

    try {
      await teamService.removeCollaborator(teamId, existingCollaborator.id);
      
      if (onCollaboratorRemoved) {
        onCollaboratorRemoved();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'Failed to remove collaborator';
      setServerError(errorMessage);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleCancelInvitation = async () => {
    if (!pendingInvitation || !isMainCoach) return;

    const confirmed = window.confirm(
      `Are you sure you want to cancel the invitation to ${pendingInvitation.email}?`
    );

    if (!confirmed) return;

    setIsCancelling(true);
    setServerError('');

    try {
      // TODO: Implement cancel invitation API endpoint
      // For now, just call the callback
      if (onInvitationCancelled) {
        onInvitationCancelled();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'Failed to cancel invitation';
      setServerError(errorMessage);
    } finally {
      setIsCancelling(false);
    }
  };

  // If there's already a collaborating coach, show info instead of form
  if (existingCollaborator) {
    return (
      <FormContainer>
        <FormTitle>Collaborating Coach</FormTitle>
        <CoachInfo>
          <CoachLabel>Current Collaborator:</CoachLabel>
          <CoachName>
            {existingCollaborator.firstName} {existingCollaborator.lastName}
          </CoachName>
          <CoachLabel style={{ marginTop: 'var(--spacing-xs)' }}>
            Email: {existingCollaborator.email}
          </CoachLabel>
        </CoachInfo>
        {serverError && <ErrorMessage>{serverError}</ErrorMessage>}
        {isMainCoach ? (
          <>
            <InfoMessage>
              This team already has a collaborating coach. Remove the current collaborator to invite a different coach.
            </InfoMessage>
            <FlexContainer $justify="flex-end">
              <Button
                type="button"
                $variant="danger"
                onClick={handleRemoveCollaborator}
                disabled={isRemoving}
              >
                {isRemoving ? 'Removing...' : 'Remove Collaborator'}
              </Button>
            </FlexContainer>
          </>
        ) : (
          <InfoMessage>
            You are a collaborating coach on this team. Only the team owner can manage collaborators.
          </InfoMessage>
        )}
      </FormContainer>
    );
  }

  // If there's a pending invitation, show status instead of form
  if (pendingInvitation) {
    const expirationDate = new Date(pendingInvitation.expiresAt);
    const isExpired = expirationDate < new Date();

    return (
      <FormContainer>
        <FormTitle>Pending Invitation</FormTitle>
        <CoachInfo>
          <CoachLabel>Invitation Sent To:</CoachLabel>
          <CoachName>
            {pendingInvitation.firstName} {pendingInvitation.lastName}
          </CoachName>
          <CoachLabel style={{ marginTop: 'var(--spacing-xs)' }}>
            Email: {pendingInvitation.email}
          </CoachLabel>
          <CoachLabel style={{ marginTop: 'var(--spacing-xs)' }}>
            {isExpired 
              ? `Expired on ${expirationDate.toLocaleDateString()}`
              : `Expires on ${expirationDate.toLocaleDateString()}`
            }
          </CoachLabel>
        </CoachInfo>
        {serverError && <ErrorMessage>{serverError}</ErrorMessage>}
        {isMainCoach ? (
          <>
            <InfoMessage>
              {isExpired 
                ? 'This invitation has expired. Cancel it to send a new invitation.'
                : 'An invitation has been sent. The coach will receive an email with instructions to join your team.'
              }
            </InfoMessage>
            <FlexContainer $justify="flex-end">
              <Button
                type="button"
                $variant="secondary"
                onClick={handleCancelInvitation}
                disabled={isCancelling}
              >
                {isCancelling ? 'Cancelling...' : 'Cancel Invitation'}
              </Button>
            </FlexContainer>
          </>
        ) : (
          <InfoMessage>
            You are a collaborating coach on this team. Only the team owner can manage invitations.
          </InfoMessage>
        )}
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <FormTitle>Invite Collaborating Coach</FormTitle>
      <InfoMessage>
        Invite another coach to collaborate on this team. They will have read-only access to view players and assessments.
      </InfoMessage>

      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
      {serverError && <ErrorMessage>{serverError}</ErrorMessage>}

      <div>
        <FormGroup>
          <Label htmlFor="firstName">First Name *</Label>
          <Input
            id="firstName"
            type="text"
            {...register('firstName', {
              required: 'First name is required',
              minLength: {
                value: 2,
                message: 'First name must be at least 2 characters',
              },
            })}
          />
          {errors.firstName && <ErrorMessage>{errors.firstName.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="lastName">Last Name *</Label>
          <Input
            id="lastName"
            type="text"
            {...register('lastName', {
              required: 'Last name is required',
              minLength: {
                value: 2,
                message: 'Last name must be at least 2 characters',
              },
            })}
          />
          {errors.lastName && <ErrorMessage>{errors.lastName.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="email">Email Address *</Label>
          <Input
            id="email"
            type="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            })}
          />
          {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
        </FormGroup>

        <FlexContainer $justify="flex-end">
          <Button type="button" onClick={handleSubmit(onSubmit)} disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </FlexContainer>
      </div>
    </FormContainer>
  );
};
